/**
 * Person ↔ company link management (issue #2124).
 *
 * Used on the person card (companies) and the company card (contact persons).
 * Mutations go through the existing company person-link hooks — add, end
 * (`validTo`) and delete an incorrect entry.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  FormControlLabel,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PhoneIcon from '@mui/icons-material/Phone';
import { describeApiError } from '@/lib/describeApiError';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DetailSection } from '@/components/common/DetailSection';
import { FormDialog } from '@/components/common/FormDialog';
import {
  useCompanies,
  useCompanyPersonLinks,
  useCreateCompanyPersonLink,
  useRemoveCompanyPersonLink,
  useUpdateCompanyPersonLink,
  type Company,
  type CompanyPersonLink,
} from '@/hooks/useCompanies';
import { queryKeys } from '@/hooks/queryKeys';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotify } from '@/hooks/useNotify';
import {
  usePersonCompanies,
  usePersons,
  useUpdatePerson,
  type Person,
  type PersonCompanyLink,
} from '@/hooks/usePersons';

interface PersonCompanyLinksSectionProps {
  personId?: string;
  companyId?: string;
}

interface DisplayLink {
  id: string;
  companyId: string;
  personId: string;
  name: string;
  role: string | null;
  isPrimary: boolean;
  validTo: string | null;
  /** The contact person's phone; only the company card lists people. */
  phone: string | null;
}

type LinkPatch = { role?: string | null; isPrimary?: boolean };

function fromPersonCompanyLink(link: PersonCompanyLink, personId: string): DisplayLink {
  return {
    id: link.id,
    companyId: link.companyId,
    personId,
    name: link.companyName,
    role: link.role,
    isPrimary: link.isPrimary,
    validTo: link.validTo,
    phone: null,
  };
}

function fromCompanyPersonLink(link: CompanyPersonLink, companyId: string): DisplayLink {
  return {
    id: link.id,
    companyId,
    personId: link.personId,
    name: [link.firstName, link.lastName].filter(Boolean).join(' ') || '—',
    role: link.role,
    isPrimary: link.isPrimary,
    validTo: link.validTo,
    phone: link.phone,
  };
}

export function PersonCompanyLinksSection({ personId, companyId }: PersonCompanyLinksSectionProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const queryClient = useQueryClient();

  const isPersonView = Boolean(personId);

  const personCompaniesQuery = usePersonCompanies(personId);
  const companyPeopleQuery = useCompanyPersonLinks(companyId);

  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);
  const [role, setRole] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const [linkToDelete, setLinkToDelete] = useState<DisplayLink | null>(null);
  // Company card: a contact can be created here with a phone, and a phone typed
  // for an existing contact is saved on the person (#3401).
  const [newPerson, setNewPerson] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  const companiesQuery = useCompanies(
    { q: debouncedSearch || undefined },
    { enabled: addOpen && isPersonView },
  );
  const personsQuery = usePersons(
    { q: debouncedSearch || undefined },
    { enabled: addOpen && !isPersonView },
  );

  const createLink = useCreateCompanyPersonLink();
  const updateLink = useUpdateCompanyPersonLink();
  const removeLink = useRemoveCompanyPersonLink();
  const updatePerson = useUpdatePerson();

  const links: DisplayLink[] = personId
    ? (personCompaniesQuery.data?.data ?? []).map((link) => fromPersonCompanyLink(link, personId))
    : companyId
      ? (companyPeopleQuery.data?.data ?? []).map((link) => fromCompanyPersonLink(link, companyId))
      : [];

  const isLoading = isPersonView ? personCompaniesQuery.isLoading : companyPeopleQuery.isLoading;

  const companyOptions = companiesQuery.data?.pages.flatMap((page) => page.data) ?? [];
  const personOptions = personsQuery.data?.pages.flatMap((page) => page.data) ?? [];

  const resetAddForm = () => {
    setSelectedCompany(null);
    setSelectedPerson(null);
    setSearch('');
    setRole('');
    setIsPrimary(false);
    setNewPerson(false);
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
  };

  const handleCloseAdd = () => {
    resetAddForm();
    setAddOpen(false);
  };

  const invalidate = (targetCompanyId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.companies.personLinks(targetCompanyId) });
    if (personId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.persons.companies(personId) });
    }
  };

  const handleAdd = async () => {
    const targetCompanyId = isPersonView ? selectedCompany?.id : companyId;
    if (!targetCompanyId) return;
    const linkFields = { role: role.trim() || null, isPrimary };

    try {
      if (!isPersonView && newPerson) {
        await createLink.mutateAsync({
          companyId: targetCompanyId,
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone: phone.trim() || null,
            email: email.trim() || null,
            ...linkFields,
          },
        });
      } else {
        const targetPersonId = isPersonView ? personId : selectedPerson?.id;
        if (!targetPersonId) return;
        const typedPhone = phone.trim();
        if (!isPersonView && selectedPerson && typedPhone !== (selectedPerson.phone ?? '')) {
          await updatePerson.mutateAsync({ id: selectedPerson.id, data: { phone: typedPhone || null } });
        }
        await createLink.mutateAsync({
          companyId: targetCompanyId,
          data: { personId: targetPersonId, ...linkFields },
        });
      }
      invalidate(targetCompanyId);
      notify.success(t('personCompanyLinks.addedSuccess'));
      handleCloseAdd();
    } catch (error) {
      notify.error(t('error'), describeApiError(error, t, { fallback: t('personCompanyLinks.addFailed') }));
    }
  };

  const handleEnd = async (link: DisplayLink) => {
    try {
      await updateLink.mutateAsync({
        companyId: link.companyId,
        linkId: link.id,
        data: { validTo: new Date().toISOString() } as unknown as LinkPatch,
      });
      invalidate(link.companyId);
      notify.success(t('personCompanyLinks.endedSuccess'));
    } catch (error) {
      notify.error(t('error'), describeApiError(error, t, { fallback: t('personCompanyLinks.endFailed') }));
    }
  };

  const handleDelete = async () => {
    if (!linkToDelete) return;
    try {
      await removeLink.mutateAsync({
        companyId: linkToDelete.companyId,
        linkId: linkToDelete.id,
      });
      invalidate(linkToDelete.companyId);
      notify.success(t('personCompanyLinks.deletedSuccess'));
      setLinkToDelete(null);
    } catch (error) {
      notify.error(t('error'), describeApiError(error, t, { fallback: t('personCompanyLinks.deleteFailed') }));
    }
  };

  const title = isPersonView
    ? t('personCompanyLinks.titleForPerson')
    : t('personCompanyLinks.titleForCompany');
  const addLabel = isPersonView
    ? t('personCompanyLinks.addCompany')
    : t('personCompanyLinks.addPerson');
  const emptyLabel = isPersonView
    ? t('personCompanyLinks.noneForPerson')
    : t('personCompanyLinks.noneForCompany');
  const addSubmitDisabled = isPersonView
    ? !selectedCompany
    : newPerson
      ? !firstName.trim() || !lastName.trim()
      : !selectedPerson;

  return (
    <>
      <DetailSection
        title={title}
        action={
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
            {addLabel}
          </Button>
        }
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : links.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            {emptyLabel}
          </Typography>
        ) : (
          <List disablePadding>
            {links.map((link) => (
              <ListItem
                key={link.id}
                disablePadding
                secondaryAction={
                  <Stack direction="row" spacing={0.5}>
                    {link.phone && (
                      <Tooltip title={t('personCompanyLinks.call', { phone: link.phone })}>
                        <IconButton
                          edge="end"
                          component="a"
                          href={`tel:${link.phone}`}
                          aria-label={t('personCompanyLinks.call', { phone: link.phone })}
                        >
                          <PhoneIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!link.validTo && (
                      <Tooltip title={t('personCompanyLinks.end')}>
                        <IconButton
                          edge="end"
                          aria-label={t('personCompanyLinks.end')}
                          onClick={() => {
                            void handleEnd(link);
                          }}
                        >
                          <EventBusyIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('personCompanyLinks.deleteIncorrect')}>
                      <IconButton
                        edge="end"
                        aria-label={t('personCompanyLinks.deleteIncorrect')}
                        onClick={() => setLinkToDelete(link)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
                sx={{ pr: link.phone ? 17 : 12 }}
              >
                <ListItemButton
                  onClick={() =>
                    navigate(
                      isPersonView
                        ? `/parties/companies/${link.companyId}`
                        : `/parties/persons/${link.personId}`,
                    )
                  }
                >
                  <ListItemText
                    primary={link.name}
                    secondary={[link.role, link.phone].filter(Boolean).join(' · ') || undefined}
                  />
                  <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
                    {link.isPrimary && (
                      <Chip size="small" color="primary" label={t('personCompanyLinks.primary')} />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      color={link.validTo ? 'default' : 'success'}
                      label={link.validTo ? t('personCompanyLinks.ended') : t('personCompanyLinks.active')}
                    />
                  </Stack>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DetailSection>

      <FormDialog
        open={addOpen}
        onClose={handleCloseAdd}
        title={addLabel}
        isDirty={
          !!selectedCompany || !!selectedPerson || role.length > 0 || isPrimary || !!firstName || !!lastName || !!phone || !!email
        }
        onSubmit={() => void handleAdd()}
        isLoading={createLink.isPending}
        submitDisabled={addSubmitDisabled}
        submitLabel={t('add')}
        maxWidth="sm"
      >
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {!isPersonView && (
              <ToggleButtonGroup
                value={newPerson ? 'new' : 'existing'}
                exclusive
                fullWidth
                size="small"
                onChange={(_e, value: 'new' | 'existing' | null) => {
                  if (!value) return;
                  setNewPerson(value === 'new');
                  setSelectedPerson(null);
                  setSearch('');
                  setPhone('');
                }}
              >
                <ToggleButton value="existing">{t('personCompanyLinks.existingPerson')}</ToggleButton>
                <ToggleButton value="new">{t('personCompanyLinks.newPerson')}</ToggleButton>
              </ToggleButtonGroup>
            )}
            {isPersonView ? (
              <Autocomplete<Company>
                options={companyOptions}
                loading={companiesQuery.isLoading}
                value={selectedCompany}
                onChange={(_e, value) => setSelectedCompany(value)}
                inputValue={search}
                onInputChange={(_e, value) => setSearch(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label={t('personCompanyLinks.selectCompany')} required />
                )}
              />
            ) : newPerson ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label={t('personCompanyLinks.firstName')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label={t('personCompanyLinks.lastName')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  fullWidth
                />
              </Stack>
            ) : (
              <Autocomplete<Person>
                options={personOptions}
                loading={personsQuery.isLoading}
                value={selectedPerson}
                onChange={(_e, value) => {
                  setSelectedPerson(value);
                  setPhone(value?.phone ?? '');
                }}
                inputValue={search}
                onInputChange={(_e, value) => setSearch(value)}
                getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField {...params} label={t('personCompanyLinks.selectPerson')} required />
                )}
              />
            )}
            {!isPersonView && (
              <TextField
                label={t('personCompanyLinks.phone')}
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                helperText={!newPerson && selectedPerson ? t('personCompanyLinks.phoneSavedToPerson') : undefined}
                fullWidth
              />
            )}
            {!isPersonView && newPerson && (
              <TextField
                label={t('personCompanyLinks.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
              />
            )}
            <TextField
              label={t('personCompanyLinks.role')}
              placeholder={t('personCompanyLinks.rolePlaceholder')}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
              }
              label={t('personCompanyLinks.makePrimary')}
            />
          </Stack>
        </DialogContent>
      </FormDialog>

      <ConfirmDialog
        open={!!linkToDelete}
        title={t('personCompanyLinks.deleteConfirmTitle')}
        message={t('personCompanyLinks.deleteConfirmMessage', {
          name: linkToDelete?.name ?? '',
        })}
        confirmText={t('delete')}
        confirmColor="error"
        loading={removeLink.isPending}
        onConfirm={() => void handleDelete()}
        onCancel={() => setLinkToDelete(null)}
      />
    </>
  );
}
